import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Container, Header, Title, Subtitle, Toolbar, ToolbarGroup, ToolbarLabel,
  ViewSwitcher, ViewButton, Select, NavButton, PrimaryButton, DateDisplay,
  Panel, DayTabs, DayTab, EmptyState, LoadingState, ErrorState, StatsBar, Stat, DropHint,
  CampusPicker, CampusTrigger, CampusCount, CampusMenu, CampusOption, CampusMenuFooter,
  RefreshLayer, RefreshBadge,
} from './Timetable.styles';
import { COLORS, NO_TEACHER_COLOR, assignTeacherColours } from './palette';
import GridView from './GridView';
import { AdminNotesBubble, campusHasAdminNotes, useAdminNoteHover } from './AdminNotes';
import WeekView from './WeekView';
import ListView, { toCsv } from './ListView';
import LessonModal from './LessonModal';
import AddLessonModal from './AddLessonModal';
import ScopeModal from './ScopeModal';
import {
  getTimetable, VIEW_OPTIONS, GRID_VIEWS, LESSON_TYPE_OPTIONS, LESSON_STATUS_OPTIONS,
  applyToOccurrence, updateSchedule, updateSlot, isRecurring, isSpecialSlot,
  hasConflict, isBusy
} from '../../../api/timetableApi';
import { listCampuses } from '../../../api/campusPortalApi';
import { breakpoints } from '../../../styles/theme';
import { getMyProfile } from '../../../api/userProfileApi';
import { canDeleteCourseGroup } from '../../../utils/portalPermissions';

/**
 * Unified campus timetable — one dataset, four views.
 *
 * Room View, Teacher View, Week View and List View all render the same `occurrences` array
 * fetched once per week; switching view re-renders but does not refetch. That is the whole
 * point of the design: these are four angles on one schedule, not four systems.
 */

const ADMIN_ROLES = ['portal_developer', 'portal_system_admin', 'portal_director', 'portal_manager', 'portal_admin'];
const TEACHER_ROLE = 'portal_teacher';
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const EMPTY_FILTERS = {
  teacherId: '', room: '', year: '', lessonType: '', status: '', includeCancelled: true, conflictsOnly: false
};

const toDateKey = (date) => [
  date.getFullYear(),
  String(date.getMonth() + 1).padStart(2, '0'),
  String(date.getDate()).padStart(2, '0')
].join('-');

/** Monday of the week containing `date`. */
const startOfWeek = (date) => {
  const result = new Date(date);
  const offset = (result.getDay() + 6) % 7; // Sunday(0) -> 6
  result.setDate(result.getDate() - offset);
  result.setHours(0, 0, 0, 0);
  return result;
};

const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const formatRange = (start, end) => {
  const options = { day: 'numeric', month: 'short' };
  return `${start.toLocaleDateString('en-AU', options)} – ${end.toLocaleDateString('en-AU', { ...options, year: 'numeric' })}`;
};

const Timetable = () => {
  const auth = useAuth();

  // Roles come from the Cognito groups in the ID token — the same claim the backend
  // authorises on, and what every other portal page reads. UserRolesPortal is a mirror of
  // those groups and can fall out of step with them (a role added in Cognito but not
  // written to the table), which would otherwise lock a genuine admin out of a page the
  // API is perfectly happy to serve.
  const roles = useMemo(
    () => auth.user?.profile?.['cognito:groups'] || [],
    [auth.user]
  );

  // A phone gets the list first. Room View is a wall of columns that has to be scrolled
  // sideways to read, whereas the list folds into one card per class at that width — which is
  // the shape a teacher checking their evening actually wants. One tap switches back, and the
  // choice is only the starting view, not a restriction.
  const [view, setView] = useState(
    () => (typeof window !== 'undefined' && window.innerWidth <= breakpoints.phone ? 'list' : 'room')
  );
  const [colourBy, setColourBy] = useState('teacher');
  const [orientation, setOrientation] = useState('vertical');
  // Per Room view: which single room (or teacher) is spread across the week
  const [singleKey, setSingleKey] = useState('');
  const [singleAxis, setSingleAxis] = useState('room');
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [selectedDay, setSelectedDay] = useState(() => (new Date().getDay() + 6) % 7);

  const [campuses, setCampuses] = useState([]);
  // Several campuses at once, for whoever is allowed more than one. A single-campus admin
  // simply always has one entry here.
  const [campusIds, setCampusIds] = useState([]);
  const [campusMenuOpen, setCampusMenuOpen] = useState(false);

  const [data, setData] = useState({ occurrences: [], rooms: [], teachers: [], multiCampus: false });
  // `loading` blanks the page; `refreshing` keeps the grid on screen and dims it. Only the
  // very first fetch of the session blanks anything — see load().
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const [selected, setSelected] = useState(null);
  const [adding, setAdding] = useState(false);
  const [addPrefill, setAddPrefill] = useState({});
  // A drag that still needs the "this week / whole term" answer
  const [pending, setPending] = useState(null);
  const [pendingSaving, setPendingSaving] = useState(false);
  const [pendingError, setPendingError] = useState('');
  const [dropDay, setDropDay] = useState(null);
  // This user's own UserPortal id, which is what occurrences carry as a teacher id.
  const [myTeacherId, setMyTeacherId] = useState('');
  // Gates the load effect so a failed profile/campus lookup still resolves the page
  // instead of leaving it on "Loading…" forever.
  const [ready, setReady] = useState(false);

  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);
  const canEdit = useMemo(() => roles.some(role => ADMIN_ROLES.includes(role)), [roles]);

  // Campus staff only. This grid is every class the campus runs — who teaches what, which
  // rooms are booked, and each class's full roll — so it is not opened to teachers, who get
  // their own week under My Timetable instead. The backend refuses them too; hiding the page
  // alone would have meant nothing, since the API was the thing handing out the data.
  //
  // isTeacher still matters for somebody who manages *and* teaches: the grid opens filtered to
  // their own classes, which is the view they want first.
  const isTeacher = useMemo(() => roles.includes(TEACHER_ROLE), [roles]);
  const canView = canEdit;

  // Deleting a whole course group from the lesson detail is manager-and-above only — the
  // same set the course-group API enforces on its `delete` operation. Read from the token
  // groups rather than the role context, which briefly reports its default role on mount.
  const canDeleteGroup = useMemo(() => canDeleteCourseGroup(roles), [roles]);

  // The campus popover is a toolbar control, not a modal — clicking away closes it. (The
  // no-backdrop-close rule applies to the page's modals, which use BaseModal.)
  const campusMenuRef = useRef(null);
  useEffect(() => {
    if (!campusMenuOpen) return undefined;
    const onPointerDown = (event) => {
      if (!campusMenuRef.current?.contains(event.target)) setCampusMenuOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setCampusMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [campusMenuOpen]);

  // ---- Data loading -------------------------------------------------------

  // Once a grid has been drawn it stays drawn. Replacing it with a "Loading…" panel on every
  // week change and after every drag unmounts the whole view: the screen flashes, the scroll
  // position is lost, and a one-field edit looks like a page reload. Later fetches therefore
  // leave the old week on screen, dimmed and inert, until the new one is ready.
  const hasLoadedRef = useRef(false);
  const load = useCallback(async (targetCampusIds, start) => {
    if (hasLoadedRef.current) setRefreshing(true); else setLoading(true);
    setError('');
    try {
      const result = await getTimetable({
        campusIds: targetCampusIds,
        fromDate: toDateKey(start),
        toDate: toDateKey(addDays(start, 6))
      });
      setData(result);
      hasLoadedRef.current = true;
    } catch (e) {
      setError(e.message || 'Could not load the timetable');
      // A failed refresh clears the grid rather than leaving a stale week looking current.
      setData({ occurrences: [], rooms: [], teachers: [], multiCampus: false });
      hasLoadedRef.current = false;
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Read inside the mount effect without making it re-run when the token object identity
  // changes; the groups themselves do not change within a session.
  const unrestrictedRef = useRef(false);
  unrestrictedRef.current = roles.some(
    role => role === 'portal_system_admin' || role === 'portal_developer'
  );

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const [profile, campusList] = await Promise.all([
          getMyProfile().catch(() => null),
          listCampuses().catch(() => [])
        ]);
        if (cancelled) return;

        // Campus-restricted roles only get their own campuses in the switcher. The backend
        // refuses anything else anyway; this just keeps the 403 out of the dropdown.
        const unrestricted = unrestrictedRef.current;
        setMyTeacherId(profile?.id || '');
        const ownIds = profile?.campusIds?.length
          ? profile.campusIds
          : (profile?.campusId ? [profile.campusId] : []);
        const available = unrestricted ? campusList : campusList.filter(c => ownIds.includes(c.id));
        setCampuses(available);

        // Open on one campus even for a super user. Every campus at once is a legitimate
        // thing to ask for but a poor landing page — ~340 classes across 27 room columns —
        // so the cross-campus view is something you switch into, not something you inherit.
        const own = (profile?.campusIds?.length ? profile.campusIds[0] : '')
          || profile?.campusId
          || (available[0]?.id || '');
        setCampusIds(own ? [own] : []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load your profile');
      } finally {
        if (!cancelled) setReady(true);
      }
    };

    init();
    return () => { cancelled = true; };
  }, []);

  // A teacher lands on their own week, not on 34 classes they mostly do not teach. This is
  // the existing teacher filter, pre-set once — clearing it gives them the campus grid,
  // which is exactly the escape hatch we want them to have. Guarded by a ref so re-clearing
  // the filter is not undone on the next render.
  const ownFilterApplied = useRef(false);
  useEffect(() => {
    if (ownFilterApplied.current || canEdit || !isTeacher || !myTeacherId) return;
    ownFilterApplied.current = true;
    setFilters(prev => ({ ...prev, teacherId: myTeacherId }));
  }, [canEdit, isTeacher, myTeacherId]);

  // Depend on the joined ids, not the array: a new array with the same contents would
  // otherwise refetch on every render.
  const campusKey = campusIds.join(',');

  useEffect(() => {
    if (!ready) return;
    load(campusKey ? campusKey.split(',') : [], weekStart);
  }, [ready, campusKey, weekStart, load]);

  const reload = useCallback(
    () => load(campusKey ? campusKey.split(',') : [], weekStart),
    [load, campusKey, weekStart]
  );

  // ---- Derived ------------------------------------------------------------

  /**
   * Card colour.
   *
   * By teacher (default): an admin scanning Room View wants to see at a glance where one
   * person is, so the same teacher keeps the same colour everywhere. Which colour they get is
   * decided by graph colouring over the week's adjacencies, so teachers whose classes land
   * next to each other are the pairs pushed furthest apart — see `assignTeacherColours`.
   *
   * By class: one colour per course group.
   *
   * Both are remembered for the life of the page. Recolouring on every fetch would mean a
   * teacher changing colour when you page to the next week and back, which defeats the point
   * of colouring by teacher at all — so an assignment, once shown, is kept until reload and
   * only teachers not seen yet get a new colour.
   */
  const teacherColours = useRef(new Map());
  const groupColours = useRef(new Map());

  const colorOf = useMemo(() => {
    const held = teacherColours.current;
    const unseen = data.teachers.some(teacher => !held.has(teacher.id));
    if (unseen) {
      const assigned = assignTeacherColours(
        data.teachers,
        data.occurrences,
        data.rooms.map(room => room.key),
        held
      );
      assigned.forEach((colour, id) => held.set(id, colour));
    }

    return (occurrence) => {
      if (colourBy === 'teacher') {
        const first = (occurrence.teachers || [])[0];
        if (!first?.id) return NO_TEACHER_COLOR;
        return held.get(first.id) || NO_TEACHER_COLOR;
      }
      // Course groups are assigned lazily on first sight and then kept, for the same reason.
      const key = occurrence.courseGroupId || occurrence.lessonId || occurrence.id;
      const groups = groupColours.current;
      if (!groups.has(key)) groups.set(key, COLORS[groups.size % COLORS.length]);
      return groups.get(key);
    };
  }, [colourBy, data.teachers, data.occurrences, data.rooms]);

  const years = useMemo(() => {
    const set = new Set(data.occurrences.map(o => o.year).filter(y => y !== null && y !== undefined));
    return [...set].sort((a, b) => a - b);
  }, [data.occurrences]);

  /**
   * Filtering happens here rather than server-side so changing a filter is instant and does
   * not spend a request — the week's data is already in memory.
   */
  const filtered = useMemo(() => data.occurrences.filter(occurrence => {
    if (filters.teacherId && !(occurrence.teachers || []).some(t => t.id === filters.teacherId)) return false;
    if (filters.room && occurrence.roomKey !== filters.room) return false;
    if (filters.year !== '' && String(occurrence.year) !== String(filters.year)) return false;
    if (filters.lessonType && occurrence.lessonType !== filters.lessonType) return false;
    if (filters.status && occurrence.status !== filters.status) return false;
    if (!filters.includeCancelled && occurrence.status === 'cancelled') return false;
    if (filters.conflictsOnly && !hasConflict(occurrence)) return false;
    return true;
  }), [data.occurrences, filters]);

  const conflictCount = useMemo(
    () => data.occurrences.filter(o => hasConflict(o) && o.status !== 'cancelled' && !isBusy(o)).length,
    [data.occurrences]
  );

  const dayDate = useMemo(() => addDays(weekStart, selectedDay), [weekStart, selectedDay]);
  const dayKey = useMemo(() => toDateKey(dayDate), [dayDate]);

  // Room and Teacher views show one day at a time; Week and List show the whole range.
  // Cross-campus "busy" blocks only belong on the teacher axis — in any other view they
  // would land in an Unassigned room or pad the class list.
  const visible = useMemo(() => {
    const scoped = view === 'teacher' ? filtered : filtered.filter(o => !isBusy(o));
    if (view === 'room' || view === 'teacher') return scoped.filter(o => o.date === dayKey);
    // Per Room keeps the whole week but narrows to the one room/teacher being inspected.
    if (view === 'single') {
      return scoped.filter(o => (singleAxis === 'room'
        ? o.roomKey === singleKey
        : (o.teachers || []).some(t => t.id === singleKey)));
    }
    return scoped;
  }, [filtered, view, dayKey, singleAxis, singleKey]);

  const localFiltered = useMemo(() => filtered.filter(o => !isBusy(o)), [filtered]);

  // Axes are rebuilt from what is actually visible so filtering also drops empty columns
  const roomAxis = useMemo(() => {
    const inUse = new Set(visible.map(o => o.roomKey).filter(Boolean));
    return data.rooms.filter(room => inUse.has(room.key));
  }, [data.rooms, visible]);


  const teacherAxis = useMemo(() => {
    const inUse = new Set();
    visible.forEach(o => (o.teachers || []).forEach(t => inUse.add(t.id)));
    return data.teachers.filter(teacher => inUse.has(teacher.id));
  }, [data.teachers, visible]);

  // True whenever the grid holds more than one campus, so room columns and cards can say
  // which site they belong to. Trust the response over local state — the backend resolves
  // "nothing selected" into the actual list.
  const multiCampus = data.multiCampus;

  const selectedCampuses = useMemo(
    () => (campusIds.length ? campuses.filter(c => campusIds.includes(c.id)) : campuses),
    [campuses, campusIds]
  );

  /** Full label, for the subtitle and the CSV filename. */
  /** The seven day columns Per Room uses as its axis. */
  const dayAxis = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const date = addDays(weekStart, i);
    return { key: toDateKey(date), name: `${DAY_LABELS[i]} ${date.getDate()}`, count: 0 };
  }), [weekStart]);

  /** Options for the Per Room picker — the rooms or teachers actually in use this week. */
  const singleOptions = useMemo(
    () => (singleAxis === 'room'
      ? data.rooms.map(r => ({
        key: r.key,
        label: multiCampus && r.campusName ? `${r.campusName} · ${r.name}` : r.name,
        count: r.count
      }))
      : data.teachers.map(t => ({ key: t.id, label: t.name || '(unnamed)', count: t.count }))),
    [singleAxis, data.rooms, data.teachers, multiCampus]
  );

  // Keep the selection valid as the week, campus or axis changes: fall back to the busiest
  // entry rather than showing an empty grid with a stale key.
  useEffect(() => {
    if (singleOptions.length === 0) return;
    if (singleOptions.some(o => o.key === singleKey)) return;
    const busiest = [...singleOptions].sort((a, b) => b.count - a.count)[0];
    setSingleKey(busiest.key);
  }, [singleOptions, singleKey]);

  const singleLabel = useMemo(
    () => singleOptions.find(o => o.key === singleKey)?.label || '',
    [singleOptions, singleKey]
  );

  const campusName = useMemo(() => {
    if (!selectedCampuses.length) return 'All campuses';
    if (selectedCampuses.length === campuses.length && campuses.length > 1) return 'All campuses';
    return selectedCampuses.map(c => c.name).join(' + ');
  }, [selectedCampuses, campuses]);

  /** Short label for the picker button, which must not grow with the selection. */
  const campusTriggerLabel = useMemo(() => {
    if (!selectedCampuses.length) return 'All campuses';
    if (selectedCampuses.length === campuses.length && campuses.length > 1) return 'All campuses';
    if (selectedCampuses.length === 1) return selectedCampuses[0].name;
    return `${selectedCampuses.length} campuses`;
  }, [selectedCampuses, campuses]);

  /** The one campus a new lesson would belong to — undefined while several are in view. */
  const writeCampus = selectedCampuses.length === 1 ? selectedCampuses[0] : null;

  const toggleCampus = (id) => {
    setCampusIds(prev => {
      const current = prev.length ? prev : campuses.map(c => c.id);
      const next = current.includes(id)
        ? current.filter(c => c !== id)
        : [...current, id];
      // Never leave the picker with nothing ticked: an empty selection means "all" to the
      // backend, which is the opposite of what unticking the last campus looks like.
      return next.length ? next : prev;
    });
  };

  // Counts per day tab, keyed on the actual dates of this week rather than on a
  // locale-formatted weekday name.
  const dayCounts = useMemo(() => {
    const index = new Map();
    for (let i = 0; i < 7; i += 1) {
      index.set(toDateKey(addDays(weekStart, i)), i);
    }
    const counts = new Array(7).fill(0);
    localFiltered.forEach(occurrence => {
      const i = index.get(occurrence.date);
      if (i !== undefined) counts[i] += 1;
    });
    return counts;
  }, [localFiltered, weekStart]);

  // ---- Handlers -----------------------------------------------------------

  const setFilter = (key) => (event) => {
    const value = event.target.value;
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const goToday = () => {
    const today = new Date();
    setWeekStart(startOfWeek(today));
    setSelectedDay((today.getDay() + 6) % 7);
  };

  /**
   * Translate a grid gesture into a write.
   *
   * Recurring classes get the "this week / whole term" question, including ones already
   * carrying a one-week override: having changed a week on its own must not cost you the
   * ability to then move the whole term. Only records with no weekly schedule behind them —
   * a standalone lesson, a special-term slot — have a single possible meaning.
   */
  const buildChanges = (item, change, scope) => {
    const changes = {};
    if (change.startTime) changes.startTime = change.startTime;
    if (change.endTime) changes.endTime = change.endTime;

    if (change.columnKey !== undefined) {
      if (view === 'teacher') {
        // A weekly schedule and a dated slot each carry one teacherId; a lesson override
        // carries a list, because a one-off can be taught by two people.
        if (scope === 'week') changes.teacherIds = [change.columnKey];
        else changes.teacherId = change.columnKey;
      } else {
        changes.room = change.columnName;
      }
    }

    if (change.dayIndex !== null && change.dayIndex !== undefined) {
      // Only a weekly schedule moves by weekday. A slot and a one-week override both name an
      // actual date.
      if (scope === 'term') {
        changes.dayOfWeek = change.dayIndex + 1;
      } else {
        changes.date = toDateKey(addDays(weekStart, change.dayIndex));
      }
    }
    return changes;
  };

  const applyChange = async (item, change, scope) => {
    const changes = buildChanges(item, change, scope);
    if (scope === 'term') {
      await updateSchedule(item, changes);
    } else if (scope === 'slot') {
      await updateSlot(item, changes);
    } else {
      await applyToOccurrence(item, changes);
    }
    await reload();
  };

  const handleMove = (item, change) => {
    // A special-term class is one dated slot with no recurrence behind it, so moving it moves
    // the slot — anything else would leave the Course Groups page showing a date the class no
    // longer runs on. An occurrence that already carries an override is left to that override,
    // which is deliberately in charge of its own date.
    const editsSlot = isSpecialSlot(item) && !item.lessonId;

    if (editsSlot || !isRecurring(item)) {
      setError('');
      applyChange(item, change, editsSlot ? 'slot' : 'week')
        .catch(e => setError(e.message || 'Could not move the class'));
      return;
    }
    setPendingError('');
    setPending({ item, change, groupBy: view });
  };

  const handleResize = (item, endTime) => handleMove(item, { startTime: item.startTime, endTime });

  const handleScope = async (scope) => {
    if (!pending) return;
    setPendingSaving(true);
    setPendingError('');
    try {
      await applyChange(pending.item, pending.change, scope);
      setPending(null);
    } catch (e) {
      setPendingError(e.message || 'Could not apply the change');
    } finally {
      setPendingSaving(false);
    }
  };

  const handleCreateAt = ({ columnName, campusId, startTime, endTime }) => {
    // A lesson belongs to exactly one campus. Room View can say which from the column that
    // was clicked; Teacher View cannot, so there it needs a single campus selected.
    const target = writeCampus || campuses.find(c => c.id === campusId);
    if (!target) return;
    setAddPrefill({
      campus: target,
      date: dayKey,
      startTime,
      endTime,
      room: view === 'room' ? columnName : ''
    });
    setAdding(true);
  };

  const handleExport = () => {
    const csv = toCsv(visible, multiCampus);
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `timetable-${campusName}-${toDateKey(weekStart)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ---- Admin Notes (long-lived, per course group) ---------------------------

  const noteHover = useAdminNoteHover();
  const { show: showNote, dismiss: dismissNote } = noteHover;
  const noteCampusIds = useMemo(
    () => new Set(campuses.filter(campusHasAdminNotes).map(c => c.id)),
    [campuses]
  );
  const getNote = useCallback(
    (item) => (noteCampusIds.has(item.campusId) ? item.adminNotes || '' : ''),
    [noteCampusIds]
  );
  const handleNoteHover = useCallback((item, rect) => {
    showNote({ rect, courseGroupId: item.courseGroupId, title: item.title, note: item.adminNotes });
  }, [showNote]);
  useEffect(() => { if (selected) dismissNote(); }, [selected, dismissNote]);
  const noteProps = {
    getNote,
    onNoteHover: handleNoteHover,
    onNoteLeave: noteHover.hide,
    onNoteDismiss: dismissNote
  };

  // ---- Render -------------------------------------------------------------

  // Per Room shows a whole week at once, so it has no day tabs — the days are the columns.
  const showDayTabs = view === 'room' || view === 'teacher';
  const isGridView = GRID_VIEWS.includes(view);

  // Staff only. The timetable is open to every campus since 2026-09-12; what is left here is
  // the check that keeps parents out, because the route itself has no guard and anyone could
  // otherwise reach the page by typing the URL. A plain message reads better than the API's 403.
  if (ready && !canView) {
    return (
      <Container>
        <Title>Timetable</Title>
        <Panel>
          <EmptyState>
            <h3>Not available</h3>
            <p>
              The campus timetable is for campus staff. If you teach here, your own week is
              under <b>Teacher &rsaquo; My Timetable</b>. If you are a parent, your child’s
              classes are on your own portal home.
            </p>
          </EmptyState>
        </Panel>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <div>
          <Title>Timetable</Title>
          <Subtitle>
            {campusName} · {formatRange(weekStart, weekEnd)}
            {showDayTabs && ` · ${DAY_LABELS[selectedDay]}`}
            {view === 'single' && singleLabel && ` · ${singleLabel}`}
          </Subtitle>
        </div>
        {canEdit && (
          <PrimaryButton
            onClick={() => setAdding(true)}
            disabled={!writeCampus}
            title={writeCampus ? undefined : 'Pick a single campus to add a lesson to it'}
          >
            + Add Lesson
          </PrimaryButton>
        )}
      </Header>

      <Toolbar>
        <ViewSwitcher>
          {VIEW_OPTIONS.map(option => (
            <ViewButton
              key={option.value}
              $active={view === option.value}
              onClick={() => setView(option.value)}
            >
              {option.label}
            </ViewButton>
          ))}
        </ViewSwitcher>

        <ToolbarGroup>
          <NavButton onClick={() => setWeekStart(prev => addDays(prev, -7))}>‹ Prev</NavButton>
          <DateDisplay>{formatRange(weekStart, weekEnd)}</DateDisplay>
          <NavButton onClick={() => setWeekStart(prev => addDays(prev, 7))}>Next ›</NavButton>
          <NavButton onClick={goToday}>Today</NavButton>
        </ToolbarGroup>

        {campuses.length > 1 && (
          <ToolbarGroup>
            <ToolbarLabel>Campus</ToolbarLabel>
            <CampusPicker ref={campusMenuRef}>
              <CampusTrigger
                type="button"
                $open={campusMenuOpen}
                onClick={() => setCampusMenuOpen(open => !open)}
              >
                {campusTriggerLabel}
                {selectedCampuses.length > 1 && (
                  <CampusCount>{selectedCampuses.length}</CampusCount>
                )}
                <span>▼</span>
              </CampusTrigger>

              {campusMenuOpen && (
                <CampusMenu>
                  {campuses.map(campus => {
                    const checked = selectedCampuses.some(c => c.id === campus.id);
                    return (
                      <CampusOption key={campus.id}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleCampus(campus.id)}
                        />
                        {campus.name}
                      </CampusOption>
                    );
                  })}
                  <CampusMenuFooter>
                    <button
                      type="button"
                      onClick={() => setCampusIds(campuses.map(c => c.id))}
                      disabled={selectedCampuses.length === campuses.length}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      onClick={() => setCampusIds(campuses.slice(0, 1).map(c => c.id))}
                      disabled={selectedCampuses.length === 1}
                    >
                      Just one
                    </button>
                  </CampusMenuFooter>
                </CampusMenu>
              )}
            </CampusPicker>
          </ToolbarGroup>
        )}
      </Toolbar>

      {view === 'single' && (
        <Toolbar>
          <ToolbarLabel>Showing</ToolbarLabel>
          <ViewSwitcher>
            <ViewButton $active={singleAxis === 'room'} onClick={() => setSingleAxis('room')}>
              A room
            </ViewButton>
            <ViewButton $active={singleAxis === 'teacher'} onClick={() => setSingleAxis('teacher')}>
              A teacher
            </ViewButton>
          </ViewSwitcher>
          <Select value={singleKey} onChange={(e) => setSingleKey(e.target.value)}>
            {singleOptions.map(option => (
              <option key={option.key} value={option.key}>
                {option.label} · {option.count}
              </option>
            ))}
          </Select>
        </Toolbar>
      )}

      <Toolbar>
        <ToolbarLabel>Filters</ToolbarLabel>

        <Select value={filters.teacherId} onChange={setFilter('teacherId')}>
          <option value="">All teachers</option>
          {data.teachers.map(teacher => (
            <option key={teacher.id} value={teacher.id}>{teacher.name || '(unnamed)'}</option>
          ))}
        </Select>

        <Select value={filters.room} onChange={setFilter('room')}>
          <option value="">All rooms</option>
          {data.rooms.map(room => (
            <option key={room.key} value={room.key}>
              {/* Two campuses can both have a "Room 3"; unqualified the list reads as a
                  duplicate rather than a choice. */}
              {multiCampus && room.campusName ? `${room.campusName} · ${room.name}` : room.name}
            </option>
          ))}
        </Select>

        <Select value={filters.year} onChange={setFilter('year')}>
          <option value="">All years</option>
          {years.map(year => (
            <option key={year} value={year}>Year {year}</option>
          ))}
        </Select>

        <Select value={filters.lessonType} onChange={setFilter('lessonType')}>
          <option value="">All lesson types</option>
          {LESSON_TYPE_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </Select>

        <Select value={filters.status} onChange={setFilter('status')}>
          <option value="">All statuses</option>
          {LESSON_STATUS_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </Select>

        <NavButton
          onClick={() => setFilters(prev => ({ ...prev, conflictsOnly: !prev.conflictsOnly }))}
          style={filters.conflictsOnly ? { borderColor: '#ef4444', color: '#b91c1c' } : undefined}
        >
          {filters.conflictsOnly ? 'Showing clashes only' : 'Clashes only'}
        </NavButton>

        <NavButton onClick={() => setFilters(EMPTY_FILTERS)}>
          Clear
        </NavButton>

        <ToolbarLabel>Colour</ToolbarLabel>
        <ViewSwitcher>
          <ViewButton $active={colourBy === 'teacher'} onClick={() => setColourBy('teacher')}>
            By teacher
          </ViewButton>
          <ViewButton $active={colourBy === 'class'} onClick={() => setColourBy('class')}>
            By class
          </ViewButton>
        </ViewSwitcher>

        {isGridView && (
          <>
            <ToolbarLabel>Timeline</ToolbarLabel>
            <ViewSwitcher>
              <ViewButton
                $active={orientation === 'vertical'}
                onClick={() => setOrientation('vertical')}
                title="Time down the page, one column per lane"
              >
                Vertical
              </ViewButton>
              <ViewButton
                $active={orientation === 'horizontal'}
                onClick={() => setOrientation('horizontal')}
                title="Time across the page, one row per lane"
              >
                Horizontal
              </ViewButton>
            </ViewSwitcher>
          </>
        )}

        {view === 'list' && (
          <NavButton onClick={handleExport} disabled={visible.length === 0}>
            Export CSV
          </NavButton>
        )}
      </Toolbar>

      {error && <ErrorState>{error}</ErrorState>}

      <StatsBar>
        <Stat><strong>{localFiltered.length}</strong> classes this week</Stat>
        {showDayTabs && (
          <Stat><strong>{visible.filter(o => !isBusy(o)).length}</strong> on {DAY_LABELS[selectedDay]}</Stat>
        )}
        <Stat><strong>{roomAxis.length}</strong> rooms in use</Stat>
        <Stat><strong>{teacherAxis.length}</strong> teachers</Stat>
        {conflictCount > 0 && (
          <Stat style={{ color: '#b91c1c' }}>
            <strong style={{ color: '#b91c1c' }}>{conflictCount}</strong> clashes
          </Stat>
        )}
      </StatsBar>

      {canEdit && showDayTabs && (
        <DropHint>
          Drag a class to another {view === 'teacher' ? 'teacher' : 'room'} or time, onto a day tab
          to change its day, or drag its bottom edge to change its length. Click empty space to add a lesson.
        </DropHint>
      )}

      {!canEdit && (
        <DropHint>
          Read only — tap a class for its room, students and any note.
          {filters.teacherId === myTeacherId && myTeacherId
            ? ' Showing your classes; clear the teacher filter to see the whole campus.'
            : ''}
        </DropHint>
      )}

      {showDayTabs && (
        <DayTabs>
          {DAY_LABELS.map((label, index) => (
            <DayTab
              key={label}
              data-day-tab={index}
              $active={selectedDay === index}
              $isDropTarget={dropDay === index}
              onClick={() => setSelectedDay(index)}
            >
              {label} {addDays(weekStart, index).getDate()}
              {dayCounts[index] > 0 ? ` · ${dayCounts[index]}` : ''}
            </DayTab>
          ))}
        </DayTabs>
      )}

      {loading ? (
        <Panel><LoadingState>Loading timetable…</LoadingState></Panel>
      ) : (
        <>
          {refreshing && <RefreshBadge>Updating…</RefreshBadge>}
          <RefreshLayer $busy={refreshing}>
          {view === 'room' && (
            <Panel>
              <GridView
                occurrences={visible}
                groupBy="room"
                axis={roomAxis}
                colorOf={colorOf}
                onSelect={setSelected}
                emptyHint={`No classes on ${DAY_LABELS[selectedDay]} for ${campusName}.`}
                canEdit={canEdit}
                multiCampus={multiCampus}
                orientation={orientation}
                onMove={handleMove}
                onResize={handleResize}
                onCreate={handleCreateAt}
                onDayHover={setDropDay}
                {...noteProps}
              />
            </Panel>
          )}

          {view === 'teacher' && (
            <Panel>
              <GridView
                occurrences={visible}
                groupBy="teacher"
                axis={teacherAxis}
                colorOf={colorOf}
                onSelect={setSelected}
                emptyHint={`No teachers are timetabled on ${DAY_LABELS[selectedDay]}.`}
                canEdit={canEdit}
                multiCampus={multiCampus}
                orientation={orientation}
                onMove={handleMove}
                onResize={handleResize}
                onCreate={handleCreateAt}
                onDayHover={setDropDay}
                {...noteProps}
              />
            </Panel>
          )}

          {view === 'single' && (
            <Panel>
              <GridView
                occurrences={visible}
                groupBy="day"
                axis={dayAxis}
                colorOf={colorOf}
                onSelect={setSelected}
                emptyHint={singleLabel
                  ? `Nothing booked in ${singleLabel} this week.`
                  : 'Pick a room to see its week.'}
                canEdit={false}
                multiCampus={multiCampus}
                orientation={orientation}
                {...noteProps}
              />
            </Panel>
          )}

          {view === 'week' && (
            <Panel style={{ padding: 0, background: 'transparent', boxShadow: 'none' }}>
              <WeekView
                occurrences={visible}
                weekStart={weekStart}
                colorOf={colorOf}
                onSelect={setSelected}
                multiCampus={multiCampus}
                {...noteProps}
              />
            </Panel>
          )}

          {view === 'list' && (
            <ListView
              occurrences={visible}
              colorOf={colorOf}
              onSelect={setSelected}
              multiCampus={multiCampus}
            />
          )}

          {!refreshing && data.occurrences.length === 0 && !error && (
            <Panel>
              <EmptyState>
                <h3>Nothing scheduled</h3>
                <p>No classes fall in this week for {campusName}.</p>
              </EmptyState>
            </Panel>
          )}
          </RefreshLayer>
        </>
      )}

      {noteHover.open && (
        <AdminNotesBubble
          key={noteHover.open.courseGroupId}
          open={noteHover.open}
          hover={noteHover}
          canEdit={canEdit}
          onSaved={() => reload()}
        />
      )}

      {selected && (
        <LessonModal
          occurrence={selected}
          canEdit={canEdit}
          adminNotesEnabled={noteCampusIds.has(selected.campusId)}
          // Whoever can change the timetable can change the term behind it; everyone else
          // who teaches the class gets the shortcut into their own copy of it instead.
          canEditGroup={canEdit}
          showClassLink={isTeacher && !canEdit}
          canDeleteGroup={canDeleteGroup}
          onClose={() => setSelected(null)}
          onSaved={reload}
        />
      )}

      {adding && (
        <AddLessonModal
          campusId={addPrefill.campus?.id || writeCampus?.id || ''}
          campusName={addPrefill.campus?.name || writeCampus?.name || ''}
          defaultDate={dayKey}
          rooms={data.rooms}
          prefill={addPrefill}
          onClose={() => { setAdding(false); setAddPrefill({}); }}
          onSaved={reload}
        />
      )}

      {pending && (
        <ScopeModal
          pending={pending}
          saving={pendingSaving}
          error={pendingError}
          onChoose={handleScope}
          onClose={() => { if (!pendingSaving) setPending(null); }}
        />
      )}
    </Container>
  );
};

export default Timetable;
